import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import ProductExperience from '@comp/product/product-experience'
import type { Capability } from '@comp/product/product-capabilities'
import type { Step } from '@comp/product/product-how-it-works'

const protocols = [
  'SSH',
  'RDP',
  'MySQL',
  'PostgreSQL',
  'MSSQL',
  'Oracle',
  'AWS',
  'Azure',
  'GCP',
  'Kubernetes',
  'Linux',
  'Windows',
]

interface HubContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: string[]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  capsEyebrow: string
  capsTitle: [string, string]
  capsBody: string
  capabilities: Capability[]
  howEyebrow: string
  howTitle: string
  howBody: string
  steps: Step[]
  intEyebrow: string
  intTitle: [string, string]
  intBody: string
  brochureCopy: string
}

const content: Record<'en' | 'tr', HubContent> = {
  en: {
    seoTitle: 'Monopam: Privileged Access Management | Monofor',
    seoDescription:
      "Monopam is Monofor's PAM platform: encrypted vault, session recording, password rotation, just-in-time access, and certificate lifecycle management, all in one console.",
    heroEyebrow: 'Privileged Access Management',
    heroTitle: ['Powerful access.', 'Precise control.'],
    heroBody:
      'Give engineers the access they need, for only as long as they need it. Keep credentials protected and every privileged session on the record.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Download product brief',
    capsEyebrow: 'Capabilities',
    capsTitle: ['Everything you need', 'for modern PAM.'],
    capsBody:
      'Monopam fronts every privileged surface (cloud consoles, database superusers, network gear, Linux roots) without forcing your team to learn new tools.',
    capabilities: [
      {
        title: 'Encrypted Vault',
        description:
          'Every credential stored in a hardened, tamper-evident vault. Zero-knowledge access by design.',
      },
      {
        title: 'Session Recording',
        description:
          'Full video and keystroke logs for every privileged session. Searchable and exportable.',
      },
      {
        title: 'Password Rotation',
        description:
          'Automated rotation on schedule, on demand, or after every privileged session.',
      },
      {
        title: 'Just-in-Time Access',
        description:
          'Time-bound, approval-gated grants. Eliminate standing privilege without slowing engineers.',
      },
      {
        title: 'MFA Step-up',
        description:
          'Risk-based MFA challenge for every elevation. Phishing-resistant factors built in.',
      },
      {
        title: 'Browser-Based Access',
        description:
          'RDP, SSH, and database sessions in any browser. No client installs, no agent fleet.',
      },
      {
        title: 'Certificate Manager',
        description:
          'Discover every TLS certificate, issue via ACME / AD CS / built-in CA, renew and deploy agentlessly.',
      },
      {
        title: 'Resource Discovery',
        description:
          'Find privileged accounts and systems across AD, Azure, and AWS before they find their way into an incident.',
      },
      {
        title: 'Policy Engine',
        description:
          'Command filtering, clipboard and file-transfer control, and approval flows enforced per session.',
      },
      {
        title: 'Kubernetes & OpenShift',
        description:
          'Short-lived kubeconfigs, verb/namespace policy, and kubectl exec recording, all with native tools.',
      },
      {
        title: 'Database Access & Masking',
        description:
          'Proxy-injected credentials, SQL-level policy and audit, and dynamic data masking for PII.',
      },
      {
        title: 'Endpoint Privilege Management',
        description:
          'JIT elevation instead of standing local admin, allow/block by publisher, offline-safe policy.',
      },
      {
        title: 'Vendor Privileged Access',
        description:
          'Invited, approval-gated, time-boxed vendor accounts with forced recording and sponsor notifications.',
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From standing privilege to zero, in three moves.',
    howBody:
      'Most teams shrink their privileged footprint by 90% within the first quarter on Monopam.',
    steps: [
      {
        title: 'Vault',
        description:
          'Move every credential, key, and secret into Monopam: encrypted at rest, never exposed in plaintext.',
      },
      {
        title: 'Approve',
        description:
          'Define who can access what, when, and for how long. Approval flows, expirations, justifications, all enforced.',
      },
      {
        title: 'Audit',
        description:
          'Watch, replay, and prove every privileged session. Auditors get the trail; you keep the speed.',
      },
    ],
    intEyebrow: 'Integrations',
    intTitle: ['Fronts every privileged', 'surface you run.'],
    intBody:
      'Monopam mediates access to every system that holds privileged credentials, and gives you one auditable surface across all of them.',
    brochureCopy:
      "Get the engineer's view: vault architecture, session pipeline, and operational requirements.",
  },
  tr: {
    seoTitle: 'Monopam: Ayrıcalıklı Erişim Yönetimi (PAM) | Monofor',
    seoDescription:
      "Monopam, Monofor'un PAM platformudur: şifreli kasa, oturum kaydı, parola rotasyonu, tam zamanında erişim ve sertifika yaşam döngüsü yönetimi, hepsi tek konsolda.",
    heroEyebrow: 'Ayrıcalıklı Erişim Yönetimi',
    heroTitle: ['Güçlü yetkiler.', 'Tam kontrol.'],
    heroBody:
      'Mühendislere ihtiyaç duydukları erişimi, yalnızca gereken süre için verin. Kimlik bilgilerini koruyun ve her ayrıcalıklı oturumu kaydedin.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Ürün dokümanını indir',
    capsEyebrow: 'Yetenekler',
    capsTitle: ['Modern PAM için', 'ihtiyacınız olan her şey.'],
    capsBody:
      'Monopam tüm ayrıcalıklı yüzeylerin önünde durur (bulut konsolları, veritabanı superuser’ları, ağ cihazları, Linux root’ları), ekibinize yeni araçlar öğretmeye zorlamadan.',
    capabilities: [
      {
        title: 'Şifreli Kasa',
        description:
          'Her kimlik bilgisi sağlamlaştırılmış, kurcalamaya karşı korumalı bir kasada. Tasarım gereği zero-knowledge erişim.',
      },
      {
        title: 'Oturum Kaydı',
        description:
          'Her ayrıcalıklı oturum için tam video ve tuş vuruşu kaydı. Aranabilir ve dışa aktarılabilir.',
      },
      {
        title: 'Parola Rotasyonu',
        description:
          'Zamanlanmış, talep üzerine veya her ayrıcalıklı oturumdan sonra otomatik rotasyon.',
      },
      {
        title: 'Tam Zamanında Erişim',
        description:
          'Süre sınırlı, onaya bağlı yetkiler. Mühendisleri yavaşlatmadan kalıcı ayrıcalığı ortadan kaldırın.',
      },
      {
        title: 'MFA ile Yükseltme',
        description:
          'Her yetki yükseltmede risk bazlı MFA doğrulaması. Kimlik avına dirençli faktörler yerleşik.',
      },
      {
        title: 'Tarayıcı Tabanlı Erişim',
        description:
          'RDP, SSH ve veritabanı oturumları herhangi bir tarayıcıda. İstemci kurulumu yok, ajan filosu yok.',
      },
      {
        title: 'Sertifika Yöneticisi',
        description:
          'Tüm TLS sertifikalarını keşfedin; ACME / AD CS / yerleşik CA ile üretin, ajansız yenileyip dağıtın.',
      },
      {
        title: 'Kaynak Keşfi',
        description:
          'AD, Azure ve AWS’teki ayrıcalıklı hesap ve sistemleri bir olaya karışmadan önce bulun.',
      },
      {
        title: 'Politika Motoru',
        description:
          'Komut filtreleme, pano ve dosya aktarımı kontrolü, oturum başına uygulanan onay akışları.',
      },
      {
        title: 'Kubernetes ve OpenShift',
        description:
          'Kısa ömürlü kubeconfig, verb/namespace politikası ve kubectl exec kaydı, hepsi yerel araçlarla.',
      },
      {
        title: 'Veritabanı Erişimi ve Maskeleme',
        description:
          'Proxy ile enjekte edilen kimlik bilgileri, SQL seviyesinde politika ve denetim, PII için dinamik maskeleme.',
      },
      {
        title: 'Uç Nokta Ayrıcalık Yönetimi (EPM)',
        description:
          'Kalıcı local admin yerine JIT yükseltme, yayıncıya göre izin/engel, çevrimdışı çalışan politika.',
      },
      {
        title: 'Tedarikçi Ayrıcalıklı Erişimi',
        description:
          'Davetli, onay kapılı, süreli tedarikçi hesapları; zorunlu kayıt ve sponsor bildirimleriyle.',
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Kalıcı ayrıcalıktan sıfıra, üç hamlede.',
    howBody:
      'Çoğu ekip Monopam ile ilk çeyrekte ayrıcalıklı ayak izini %90 küçültüyor.',
    steps: [
      {
        title: 'Kasaya Alın',
        description:
          'Her kimlik bilgisini, anahtarı ve sırrı Monopam’a taşıyın: beklemede şifreli, asla düz metin olarak açığa çıkmaz.',
      },
      {
        title: 'Onaylayın',
        description:
          'Kimin neye, ne zaman ve ne kadar süreyle erişebileceğini tanımlayın. Onay akışları, süre bitişleri, gerekçeler; hepsi uygulanır.',
      },
      {
        title: 'Denetleyin',
        description:
          'Her ayrıcalıklı oturumu izleyin, tekrar oynatın ve kanıtlayın. Denetçiler izi alır; siz hızınızı korursunuz.',
      },
    ],
    intEyebrow: 'Entegrasyonlar',
    intTitle: ['Çalıştırdığınız her ayrıcalıklı', 'yüzeyin önünde.'],
    intBody:
      'Monopam, ayrıcalıklı kimlik bilgisi barındıran her sisteme erişimi aracılar ve hepsinde tek bir denetlenebilir yüzey sunar.',
    brochureCopy:
      'Mühendis gözüyle inceleyin: kasa mimarisi, oturum hattı ve operasyonel gereksinimler.',
  },
}

type Props = { locale: string }

export default function MonopamPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  const basePath = locale === 'tr' ? '/tr' : ''
  return (
    <Layout>
      <Seo
        title={c.seoTitle}
        description={c.seoDescription}
        path={`${basePath}/pam`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/pam`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/pam`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/pam`} />
      </Head>
      <JsonLd
        id="product"
        data={{
          '@type': 'SoftwareApplication',
          name: 'Monopam',
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Web (cloud or self-hosted)',
          description:
            "Monofor's PAM platform: encrypted vault, session recording, password rotation, and just-in-time privileged access, all from one console.",
          url: 'https://monofor.com/pam',
          publisher: { '@type': 'Organization', name: 'Monofor' },
        }}
      />
      <ProductExperience product="pam" lang={locale === 'tr' ? 'tr' : 'en'} content={c} protocols={protocols} />
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
